import { useCallback, useMemo, useState } from 'react';

export interface IProps {
  initialState: Record<string, boolean>;
  requiredColumnIds?: string[];
  onChange: (newState: Record<string, boolean>) => void;
  defaultColumns: Record<string, boolean>;
}

const useColumnsVisibility = ({ initialState, defaultColumns, requiredColumnIds = [], onChange }: IProps) => {
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(initialState);
  const [columnSearchQuery, setColumnSearchQuery] = useState('');

  const handleColumnVisibilityChange = useCallback(
    (columnId: string) => {
      const newVal = {
        ...visibleColumns,
        [columnId]: !visibleColumns[columnId],
      };

      setVisibleColumns((prev) => ({
        ...prev,
        [columnId]: !prev[columnId],
      }));
      onChange?.(newVal);
    },
    [onChange, visibleColumns],
  );

  const handleToggleAllColumns = useCallback(
    (showAll: boolean) => {
      const newState = { ...visibleColumns };

      Object.keys(newState).forEach((key) => {
        if (!requiredColumnIds.includes(key)) {
          newState[key] = showAll;
        }
      });

      setVisibleColumns(newState);
      onChange?.(newState);
    },
    [visibleColumns, onChange, requiredColumnIds],
  );

  const handleResetColumns = useCallback(() => {
    setVisibleColumns(defaultColumns);
    onChange?.(defaultColumns);
  }, [onChange, defaultColumns]);

  const areAllColumnsVisible = useMemo(() => {
    const optionalColumnIds = Object.keys(visibleColumns).filter((id) => !requiredColumnIds.includes(id));

    return optionalColumnIds.every((id) => visibleColumns[id]);
  }, [visibleColumns, requiredColumnIds]);

  return {
    visibleColumns,
    columnSearchQuery,
    setColumnSearchQuery,
    handleColumnVisibilityChange,
    handleToggleAllColumns,
    handleResetColumns,
    areAllColumnsVisible,
  };
};

export default useColumnsVisibility;
